import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { Pildora } from '#components/mobile/ui/Pildora';
import { color, densidad, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategory } from '#hooks/useCategory';
import { useFormat } from '#hooks/useFormat';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

type HojaSaldoProps = {
  month: string;
  onBudgetAction: (month: string, type: string, args: unknown) => unknown;
  categoryId: string;
  onCerrar: () => void;
};

/**
 * Tocar un Disponible que no está en rojo (H24): el saldo en una pastilla y
 * dos filas, «Traer dinero de otra categoría» y «Mover a otra categoría»,
 * que abren la hoja «Mover dinero» con esta categoría ya puesta. (En rojo
 * se abre directamente «Cubrir».)
 */
export function HojaSaldo(props: HojaSaldoProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.month)}>
      <HojaSaldoContenido {...props} />
    </SheetNameProvider>
  );
}

function HojaSaldoContenido({
  month,
  onBudgetAction,
  categoryId,
  onCerrar,
}: HojaSaldoProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const dispatch = useDispatch();
  const { data: category } = useCategory(categoryId);
  const saldo =
    useSheetValue<'envelope-budget', 'leftover'>(
      envelopeBudget.catBalance(categoryId),
    ) ?? 0;

  const abrirMover = (modoInicial: 'desde' | 'hacia') => {
    onCerrar();
    dispatch(
      pushModal({
        modal: {
          name: 'mover',
          options: { month, onBudgetAction, origen: categoryId, modoInicial },
        },
      }),
    );
  };

  return (
    <Hoja
      aria-label={category?.name ?? t('Available')}
      data-testid="hoja-saldo"
      onCerrar={onCerrar}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <View style={{ minWidth: 0, flex: 1 }}>
          <Text
            style={{
              ...texto.titulo,
              color: color.fg,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {category?.name ?? ''}
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>Available</Trans>
          </Text>
        </View>
        <PrivacyFilter>
          <Pildora
            estado={saldo > 0 ? 'ok' : 'neutro'}
            tamano="grande"
            data-testid="saldo-disponible"
          >
            {format(saldo, 'financial')}
          </Pildora>
        </PrivacyFilter>
      </View>
      <FilaAccion testId="saldo-traer" onPress={() => abrirMover('hacia')}>
        <Trans>Bring money from another category</Trans>
      </FilaAccion>
      <FilaAccion testId="saldo-mover" onPress={() => abrirMover('desde')}>
        <Trans>Move to another category</Trans>
      </FilaAccion>
    </Hoja>
  );
}

function FilaAccion({
  testId,
  onPress,
  children,
}: {
  testId: string;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="bare"
      data-testid={testId}
      onPress={onPress}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: densidad.altoFila,
        padding: '0 4px',
        borderRadius: 0,
        boxShadow: `inset 0 -1px 0 ${color.line}`,
        backgroundColor: 'transparent',
        color: color.fg,
      }}
    >
      <Text style={{ ...densidad.nombre, textAlign: 'left' }}>{children}</Text>
      <Icono nombre="cr" size={18} style={{ color: color.fg2 }} />
    </Button>
  );
}
